import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  TableContainer,
  Table,
  Thead,
  Tr,
  Td,
  Th,
  Tbody,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { db, auth } from '../api';
import { useAuthState } from 'react-firebase-hooks/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import CustomDatePicker from './CustomDatePicker';

function ModalCashierStats({ isOpen, onClose }) {
  const [user] = useAuthState(auth);
  const [from, setFrom] = useState(new Date(new Date().setHours(0, 0, 0, 0)));
  const [to, setTo] = useState(new Date(new Date().setHours(23, 59, 59, 999)));
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({});
  // const [refundedStats, setRefundedStats] = useState({});
  const [error, setError] = useState('');
  const [hasCalculated, setHasCalculated] = useState(false);

  const isDisabled = from > to || !user;

  // Reset dello stato quando il modal si apre
  useEffect(() => {
    if (isOpen) {
      setStats({});
      // setRefundedStats({});
      setError('');
      setHasCalculated(false);
    }
  }, [isOpen]);

  const calculateStats = async () => {
    if (!user) {
      setError('Utente non autenticato');
      return;
    }

    setIsLoading(true);
    setError('');
    setStats({});
    // setRefundedStats({});

    try {
      // Query per trovare tutti gli ordini nell'intervallo di date
      const ordersQuery = query(
        collection(db, 'order'),
        where('createdAt', '>=', new Date(from)),
        where('createdAt', '<=', new Date(to))
      );

      const querySnapshot = await getDocs(ordersQuery);

      const totalsByPaymentType = {};
      // const refundedTotalsByPaymentType = {};

      // Calcola i totali per ogni ordine filtrando per cashierEmail
      querySnapshot.forEach((doc) => {
        const orderData = doc.data();
        
        // Filtra solo gli ordini con cashierEmail uguale all'email dell'utente loggato
        if (orderData.cashierEmail !== user.email) {
          return;
        }

        const orderArray = orderData.order || [];
        const paymentType = orderData.paymentType || 'unknown';
        // const isRefunded = orderData.status === 'refunded';

        // Seleziona il dizionario corretto in base allo stato
        // const targetDict = isRefunded ? refundedTotalsByPaymentType : totalsByPaymentType;
        const targetDict = totalsByPaymentType;

        // Inizializza il totale per questo paymentType se non esiste
        if (!targetDict[paymentType]) {
          targetDict[paymentType] = 0;
        }

        // Calcola il totale per questo ordine
        orderArray.forEach((item) => {
          const itemTotal =
            (item.menuPrice || 0) * (item.quantity || 0) +
            (item.productPrice || 0) * (item.quantity || 0);
          targetDict[paymentType] += itemTotal;
        });
      });

      setStats(totalsByPaymentType);
      // setRefundedStats(refundedTotalsByPaymentType);
      setHasCalculated(true);
    } catch (err) {
      console.error('Errore durante il calcolo delle statistiche:', err);
      setError('Errore durante il calcolo delle statistiche');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      blockScrollOnMount={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align="start" spacing={1}>
            <Text>Statistiche Cassiere</Text>
            {user?.email && (
              <Text fontSize="sm" fontWeight="normal">
                {user.email}
              </Text>
            )}
          </VStack>
        </ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize={'lg'} fontWeight={600}>
              Dalla Data
            </Text>
            <DatePicker
              showTimeSelect
              dateFormat='MMMM d, yyyy H:mm'
              showPopperArrow={false}
              selected={from}
              onChange={(_date) => {
                setFrom(_date);
              }}
              customInput={<CustomDatePicker />}
            />
            <Text fontSize={'lg'} fontWeight={600}>
              Alla Data
            </Text>
            <DatePicker
              showTimeSelect
              dateFormat='MMMM d, yyyy H:mm'
              showPopperArrow={false}
              selected={to}
              onChange={(_date) => {
                setTo(_date);
              }}
              customInput={<CustomDatePicker />}
            />

            {hasCalculated && error && (
              <Text fontSize={'lg'} color='tomato'>
                {error}
              </Text>
            )}

            {hasCalculated && Object.keys(stats).length === 0 && (
              <Text my={5}>Nessuna statistica disponibile per questo periodo.</Text>
            )}

            {hasCalculated && Object.keys(stats).length > 0 && (
              <TableContainer mt={5}>
                <Table variant='striped'>
                  <Thead>
                    <Tr>
                      <Th>Tipo di Pagamento</Th>
                      <Th isNumeric>Totale</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {Object.entries(stats).map(([paymentType, total]) => (
                      <Tr key={paymentType}>
                        <Td>{paymentType}</Td>
                        <Td isNumeric>€ {total.toFixed(2)}</Td>
                      </Tr>
                    ))}
                    <Tr fontWeight="bold">
                      <Td>TOTALE</Td>
                      <Td isNumeric>
                        € {Object.values(stats).reduce((sum, val) => sum + val, 0).toFixed(2)}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            )}

            {/* {hasCalculated && Object.keys(refundedStats).length > 0 && (
              <TableContainer mt={5}>
                <Text fontSize={'lg'} fontWeight={600} mb={3}>
                  Rimborsati
                </Text>
                <Table variant='striped'>
                  <Thead>
                    <Tr>
                      <Th>Tipo di Pagamento</Th>
                      <Th isNumeric>Totale</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {Object.entries(refundedStats).map(([paymentType, total]) => (
                      <Tr key={paymentType}>
                        <Td>{paymentType}</Td>
                        <Td isNumeric>€ {total.toFixed(2)}</Td>
                      </Tr>
                    ))}
                    <Tr fontWeight="bold">
                      <Td>TOTALE RIMBORSATI</Td>
                      <Td isNumeric>
                        € {Object.values(refundedStats).reduce((sum, val) => sum + val, 0).toFixed(2)}
                      </Td>
                    </Tr>
                  </Tbody>
                </Table>
              </TableContainer>
            )} */}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack spacing={3}>
            <Button minWidth={125} size={'lg'} onClick={onClose}>
              Chiudi
            </Button>
            <Button
              minWidth={125}
              size={'lg'}
              isLoading={isLoading}
              isDisabled={isDisabled}
              onClick={calculateStats}
            >
              Calcola
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default ModalCashierStats;
